import { FrameID } from '@tunein/web-media-player';
import { debug } from '../../../utils';
// Valid iHeart music ID3 metadata strings contain `song_spot` values `M` or `F`, per iHeart's guidance
const validIHeartSongSpots = new Set(['M', 'F']);
const IHEART_IMAGE_SIZE = 300;
const IHEART_METADATA_KEY = 'URL';
function buildIHeartImageUrl(metadata) {
    if (Number.parseInt(metadata?.TPID)) {
        return `https://i.iheart.com/v3/catalog/track/${metadata.TPID}?ops=fit(${IHEART_IMAGE_SIZE},${IHEART_IMAGE_SIZE})`;
    }
}
function isIHeartId3Metadata(id3) {
    const iHeartId3Metadata = id3[FrameID.USER_DEFINED_URL]?.[IHEART_METADATA_KEY];
    return (iHeartId3Metadata &&
        typeof iHeartId3Metadata === 'object' &&
        Boolean(iHeartId3Metadata.song_spot));
}
function parseIHeartId3Metadata(id3, startMs) {
    const iHeartId3Metadata = id3[FrameID.USER_DEFINED_URL]?.[IHEART_METADATA_KEY];
    if (!validIHeartSongSpots.has(iHeartId3Metadata.song_spot)) {
        // enforce station primary metadata if song_spot is not valid
        return {
            title: '',
            subtitle: '',
            startMs,
            usePrimaryMetadata: true,
        };
    }
    return {
        title: id3[FrameID.ARTIST],
        subtitle: id3[FrameID.TITLE],
        image: buildIHeartImageUrl(iHeartId3Metadata) || '',
        startMs,
    };
}
const AUDACY_METADATA_KEY = 'amperwave.metadata';
function isAudacyId3Metadata(id3) {
    const audacyId3Metadata = id3[FrameID.PRIVATE]?.[AUDACY_METADATA_KEY];
    return audacyId3Metadata && typeof audacyId3Metadata === 'object';
}
function parseAudacyId3Metadata(id3, startMs) {
    const { artist, title, albumArtUrl, type } = id3[FrameID.PRIVATE]?.[AUDACY_METADATA_KEY];
    return {
        title: artist || '',
        subtitle: title || '',
        image: albumArtUrl || '',
        isAdBreak: type === 'ad',
        startMs,
    };
}
function isAppleId3Metadata(id3) {
    const appleId3Metadata = id3[FrameID.USER_DEFINED_URL];
    return (appleId3Metadata &&
        typeof appleId3Metadata === 'object' &&
        'artworkURL_390x' in appleId3Metadata);
}
function parseAppleId3Metadata(id3, startMs) {
    const { artworkURL_390x: image } = id3[FrameID.USER_DEFINED_URL];
    return {
        title: id3[FrameID.ARTIST] || '',
        subtitle: id3[FrameID.TITLE] || '',
        image,
        startMs,
    };
}
// =============================================================================
//
//  MAIN PARSERS
//
// =============================================================================
function parse(id3, startMs) {
    if (isIHeartId3Metadata(id3)) {
        return parseIHeartId3Metadata(id3, startMs);
    }
    if (isAudacyId3Metadata(id3)) {
        return parseAudacyId3Metadata(id3, startMs);
    }
    if (isAppleId3Metadata(id3)) {
        return parseAppleId3Metadata(id3, startMs);
    }
    return {
        title: id3[FrameID.ARTIST] || '',
        subtitle: id3[FrameID.TITLE] || '',
        startMs,
    };
}
export function parseId3Metadata(metadata) {
    const { id3, startSeconds } = metadata;
    if (!(id3 && Number.isFinite(startSeconds))) {
        return null;
    }
    debug('Parsing ID3 metadata', metadata);
    const startMs = startSeconds ? startSeconds * 1000 : 0;
    const parsedMetadata = parse(id3, startMs);
    debug('Parsed ID3 metadata', parsedMetadata);
    return parsedMetadata;
}
